// Organiza a lista completa de coberturas de um plano (coverageList) para a
// janela "Ver cobertura completa": agrupa por assunto, formata os valores e
// separa as informações importantes (validade territorial, limite de idade...).
import { findAmounts, formatAmount, pickAmount } from './coverage.utils';
import { COMPARE_SECTIONS } from './compare.utils';

const normalize = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Nome sem pontuação, para achar itens repetidos ("Traslado médico:" x "Traslado Medico").
const nameKey = (text) => normalize(text).replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

// Siglas que continuam em maiúsculas quando convertemos textos GRITADOS.
const KEEP_UPPER = new Set(['EUA', 'USA', 'USD', 'EUR', 'DMH', 'DMHO', 'PIR', 'COVID-19', 'VIP', 'UE']);

// "INTERNACIONAL (EXCETO EUA)" -> "Internacional (exceto EUA)"
export const smartCase = (text) => {
  const value = String(text || '').trim();
  const letters = value.replace(/[^A-Za-zÀ-ú]/g, '');
  if (letters.length < 4 || letters !== letters.toUpperCase()) return value;
  const lower = value
    .split(/(\s+)/)
    .map((word) => (KEEP_UPPER.has(word.replace(/[^A-Z0-9-]/g, '')) ? word : word.toLowerCase()))
    .join('');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// Nome da cobertura sem ":" / "." / "(*)" no final.
const cleanLabel = (text) =>
  smartCase(String(text || '').trim().replace(/\s*\(\*+\)\s*$/, '').replace(/[:.\s]+$/, ''));

// Itens que não são coberturas, mas avisos (Assist Card manda vários).
const INFO_REGEX = /^(legal|\*+|\(\*\)|validade territorial|limitac|dias consecutivos)/;

// Seções da janela: as mesmas da tela de comparação + "Assistência e serviços".
const SECTIONS = [
  ...COMPARE_SECTIONS.map((section) => ({
    title: section.title,
    rows: section.rows.filter((row) => row.keywords),
  })),
];
const OTHER_SECTION = 'Assistência e serviços';

const findSection = (description) => {
  const name = normalize(description);
  if (/despesas medicas|assistencia medica/.test(name) && !/esporte|gestante|preexist/.test(name)) {
    return 'Saúde';
  }
  if (/bagagem|mala/.test(name)) return 'Bagagem e voo';
  const section = SECTIONS.find((s) =>
    s.rows.some(
      (row) =>
        row.keywords.some((kw) => name.includes(kw)) &&
        !(row.exclude || []).some((kw) => name.includes(kw))
    )
  );
  return section ? section.title : OTHER_SECTION;
};

// Formata o valor de um item: "€ 1.200" + detalhe "(€ 80 por kg)", "Incluso" etc.
const formatValue = (rawValue, isEurope) => {
  const value = String(rawValue || '').trim();
  const amount = pickAmount(findAmounts(value, 1), isEurope);
  if (amount) {
    // Detalhes entre parênteses, ex: "(mais de 8hs)", "(EUR 80/ kg)".
    // Na Assist Card vêm dois ("EUR 80/ kg" e "USD 80/ kg"): fica o da moeda do destino.
    const otherCurrency = isEurope ? /USD|US\$/ : /EUR|€/;
    const details = [...new Set((value.match(/\(([^)]+)\)/g) || []).map((d) => d.slice(1, -1).trim()))]
      .filter((d) => !/^\*+$/.test(d) && !otherCurrency.test(d))
      .slice(0, 1);
    return { status: 'amount', display: formatAmount(amount), detail: details[0] ? smartCase(details[0]) : null };
  }
  const normalized = normalize(value);
  if (/incluso no dmho/.test(normalized)) {
    return { status: 'included', display: 'Incluso', detail: 'Dentro da despesa médica' };
  }
  if (/^(sim|incluido|incluso)/.test(normalized)) return { status: 'included', display: 'Incluso' };
  if (/^(nao|sem cobertura)/.test(normalized)) return { status: 'no', display: 'Não cobre' };
  return { status: 'text', display: smartCase(value) };
};

// Resultado: { sections: [{ title, items: [{ label, display, status, detail }] }],
//             notices: [{ label, text }], footnotes: [texto] }
export const buildCoverageDetails = (insurance, isEurope) => {
  const list = insurance?.coverageList || [];
  const notices = [];
  const items = [];

  list.forEach((item) => {
    const description = String(item.description || '').trim();
    if (!description) return;
    if (INFO_REGEX.test(normalize(description))) {
      // "LEGAL" e "**" são textos jurídicos longos: mostrados sem título próprio.
      const generic = /^(legal|\*+|\(\*\))/.test(normalize(description));
      notices.push({
        label: generic ? null : cleanLabel(description),
        text: smartCase(item.value),
        footnote: generic,
      });
      return;
    }
    items.push({ description, label: cleanLabel(description), ...formatValue(item.value, isEurope) });
  });

  // Junta repetidos: se o mesmo item vem como "Incluso" e com valor, fica o com valor.
  const byName = new Map();
  items.forEach((item) => {
    const key = nameKey(item.label);
    const current = byName.get(key);
    if (!current || (current.status !== 'amount' && item.status === 'amount')) byName.set(key, item);
  });

  // A despesa médica principal sempre em primeiro lugar.
  const isMainMedical = (item) => {
    const name = normalize(item.description);
    return /despesas medicas (e|e\/ou) hospitalares/.test(name) && !/por covid|esporte|gestante|preexist/.test(name);
  };

  const order = [...SECTIONS.map((s) => s.title), OTHER_SECTION];
  const sections = order
    .map((title) => ({
      title,
      items: [...byName.values()]
        .filter((item) => findSection(item.description) === title)
        .sort((a, b) => isMainMedical(b) - isMainMedical(a)),
    }))
    .filter((section) => section.items.length);

  return {
    sections,
    notices: notices.filter((n) => !n.footnote),
    footnotes: notices.filter((n) => n.footnote).map((n) => n.text),
  };
};
